import { useState, useMemo } from 'react';
import { useTasks } from '../../../hooks/useTasks';
import type { Task, Priority } from '../../../mocks/crm';
import TaskItem from './components/TaskItem';
import AddTaskModal from './components/AddTaskModal';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

type PriorityFilter = 'all' | Priority;
type StatusFilter = 'all' | 'pending' | 'in_progress' | 'done';
type AssigneeFilter = 'all' | string;

const assignees = ['Mohammed Khalid', 'Sara Ahmed', 'Fatima Al-Zahra'];

const priorityConfig: Record<Priority, { color: string }> = {
  high: { color: '#DC2626' },
  medium: { color: '#D97706' },
  low: { color: '#059669' },
};

export default function TasksPage() {
  const { tasks, loading, toggleTask, deleteTask, createTask } = useTasks();
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  const stats = useMemo(() => ({
    total: tasks.length,
    high: tasks.filter((t) => t.priority === 'high' && t.status !== 'done').length,
    dueToday: tasks.filter((t) => t.dueDate === '2026-03-27' && t.status !== 'done').length,
    done: tasks.filter((t) => t.status === 'done').length,
    inProgress: tasks.filter((t) => t.status === 'in_progress').length,
  }), [tasks]);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      const matchPriority = priorityFilter === 'all' || t.priority === priorityFilter;
      const matchStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchAssignee = assigneeFilter === 'all' || t.assignedTo === assigneeFilter;
      const matchSearch =
        !search.trim() ||
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.linkedLeadName.toLowerCase().includes(search.toLowerCase());
      return matchPriority && matchStatus && matchAssignee && matchSearch;
    });
  }, [tasks, priorityFilter, statusFilter, assigneeFilter, search]);

  const groups = useMemo(() => {
    const today: Task[] = [];
    const tomorrow: Task[] = [];
    const thisWeek: Task[] = [];
    const later: Task[] = [];
    const done: Task[] = [];

    filtered.forEach((t) => {
      if (t.status === 'done') { done.push(t); return; }
      if (t.dueDate === '2026-03-27') { today.push(t); return; }
      if (t.dueDate === '2026-03-28') { tomorrow.push(t); return; }
      if (t.dueDate <= '2026-04-03') { thisWeek.push(t); return; }
      later.push(t);
    });

    return [
      { label: 'Today', date: '27 March 2026', tasks: today, accent: '#DC2626' },
      { label: 'Tomorrow', date: '28 March 2026', tasks: tomorrow, accent: '#D97706' },
      { label: 'This Week', date: 'Mar 29 – Apr 3', tasks: thisWeek, accent: '#7C3AED' },
      { label: 'Later', date: 'Future Tasks', tasks: later, accent: '#6B7280' },
      { label: 'Completed', date: 'Done', tasks: done, accent: '#059669' },
    ].filter((g) => g.tasks.length > 0);
  }, [filtered]);

  const handleToggle = (id: string) => {
    toggleTask(id);
  };

  const handleDelete = (id: string) => {
    deleteTask(id);
  };

  const handleAdd = async (task: Task) => {
    await createTask(task);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-white">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Task Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Track follow-ups, documentation, and team tasks</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            <i className="ri-add-line" />
            Add Task
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: 'Total Tasks', value: stats.total, icon: 'ri-task-line', color: brand },
            { label: 'High Priority', value: stats.high, icon: 'ri-alarm-warning-line', color: '#DC2626' },
            { label: 'Due Today', value: stats.dueToday, icon: 'ri-calendar-todo-line', color: '#D97706' },
            { label: 'In Progress', value: stats.inProgress, icon: 'ri-loader-4-line', color: '#7C3AED' },
            { label: 'Completed', value: stats.done, icon: 'ri-checkbox-circle-line', color: '#059669' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${s.color}15` }}>
                <i className={`${s.icon} text-base`} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-800 leading-tight">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 border-b border-gray-100 bg-white flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative min-w-[200px] max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none bg-gray-50 placeholder-gray-400"
          />
        </div>

        {/* Priority Filter */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          {(['all', 'high', 'medium', 'low'] as PriorityFilter[]).map((p) => {
            const isActive = priorityFilter === p;
            const color = p !== 'all' ? priorityConfig[p].color : brand;
            return (
              <button
                key={p}
                onClick={() => setPriorityFilter(p)}
                className="px-3 py-1.5 text-xs font-semibold capitalize rounded-lg cursor-pointer transition-all whitespace-nowrap"
                style={
                  isActive
                    ? { backgroundColor: 'white', color, fontWeight: 700 }
                    : { color: '#6B7280' }
                }
              >
                {p === 'all' ? 'All Priority' : p}
              </button>
            );
          })}
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          {([
            { id: 'all', label: 'All' },
            { id: 'pending', label: 'Pending' },
            { id: 'in_progress', label: 'In Progress' },
            { id: 'done', label: 'Done' },
          ] as { id: StatusFilter; label: string }[]).map((s) => (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg cursor-pointer transition-all whitespace-nowrap"
              style={
                statusFilter === s.id
                  ? { backgroundColor: 'white', color: brand, fontWeight: 700 }
                  : { color: '#6B7280' }
              }
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Assignee Filter */}
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white text-gray-600 focus:outline-none cursor-pointer"
        >
          <option value="all">All Agents</option>
          {assignees.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        <p className="text-xs text-gray-400 ml-auto whitespace-nowrap">{filtered.length} tasks</p>
      </div>

      {/* Task Groups */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {groups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ backgroundColor: `${brand}15` }}
            >
              <i className="ri-task-line text-2xl" style={{ color: brand }} />
            </div>
            <p className="text-base font-semibold text-gray-700 mb-1">No tasks found</p>
            <p className="text-sm text-gray-400">Try adjusting your filters or add a new task.</p>
          </div>
        )}

        {groups.map((group) => (
          <div key={group.label}>
            {/* Group Header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: group.accent }} />
                <h3 className="text-sm font-bold text-gray-700">{group.label}</h3>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${group.accent}15`, color: group.accent }}
                >
                  {group.tasks.length}
                </span>
              </div>
              <span className="text-xs text-gray-400">{group.date}</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>

            {/* Task Items */}
            <div className="space-y-2">
              {group.tasks.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={handleToggle}
                  onEdit={() => {}}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add Task Modal */}
      {showModal && (
        <AddTaskModal onClose={() => setShowModal(false)} onAdd={handleAdd} />
      )}
    </div>
  );
}
